/**
 * Lazy loading system for dynamic imports and progressive enhancement
 */

export interface LazyModuleConfig {
  name: string;
  importFn: () => Promise<any>;
  fallback?: any;
  timeout?: number;
}

export class LazyLoader {
  private modules = new Map<string, LazyModuleConfig>();
  private loadedModules = new Map<string, any>();
  private loadingPromises = new Map<string, Promise<any>>();

  /**
   * Register a lazy-loadable module
   */
  register(config: LazyModuleConfig): void {
    this.modules.set(config.name, config);
  }

  /**
   * Load a module dynamically
   */
  async load(moduleName: string): Promise<any> {
    // Return cached module if already loaded
    if (this.loadedModules.has(moduleName)) {
      return this.loadedModules.get(moduleName);
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(moduleName)) {
      return this.loadingPromises.get(moduleName);
    }

    const config = this.modules.get(moduleName);
    if (!config) {
      throw new Error(`Module '${moduleName}' not registered`);
    }

    // Create loading promise with timeout
    const loadingPromise = this.loadModuleWithTimeout(config);
    this.loadingPromises.set(moduleName, loadingPromise);

    try {
      const module = await loadingPromise;
      this.loadedModules.set(moduleName, module);
      this.loadingPromises.delete(moduleName);
      return module;
    } catch (error) {
      this.loadingPromises.delete(moduleName);
      
      // Return fallback if available
      if (config.fallback) {
        console.warn(`Failed to load module '${moduleName}', using fallback`, error);
        return config.fallback;
      }
      
      throw error;
    }
  }

  /**
   * Load module with timeout handling
   */
  private async loadModuleWithTimeout(config: LazyModuleConfig): Promise<any> {
    const timeout = config.timeout || 10000; // 10 second default
    
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Module '${config.name}' load timeout after ${timeout}ms`));
      }, timeout);
    });

    return Promise.race([config.importFn(), timeoutPromise]);
  }

  /**
   * Preload a module in the background
   */
  preload(moduleName: string): void {
    this.load(moduleName).catch(error => {
      console.warn(`Preload failed for module '${moduleName}'`, error);
    });
  }

  /**
   * Check if a module is loaded
   */
  isLoaded(moduleName: string): boolean {
    return this.loadedModules.has(moduleName);
  }

  /**
   * Check if a module is currently loading
   */
  isLoading(moduleName: string): boolean {
    return this.loadingPromises.has(moduleName);
  }

  /**
   * Get all loaded module names
   */
  getLoadedModules(): string[] {
    return Array.from(this.loadedModules.keys());
  }

  /**
   * Clear all cached modules
   */
  clear(): void {
    this.loadedModules.clear();
    this.loadingPromises.clear();
  }
}

// Global lazy loader instance
export const lazyLoader = new LazyLoader();

// Predefined lazy modules
export const LAZY_MODULES = {
  SECURITY: 'security',
  EMBED: 'embed',
} as const;

// Register common lazy modules

lazyLoader.register({
  name: LAZY_MODULES.EMBED,
  importFn: () => import('../embed/index'),
  timeout: 3000,
});
